/** Klici API. Vse poti so relativne, ker API streže tudi to stran. */
import type {
  AmortizacijskaSkupina,
  PregledZakljuckov,
  ZakljucenMesec,
  KnjizbaDelitve,
  PorabaProjektov,
  PregledPorabeProjektov,
  ProjektPoMesecu,
  PregledTemeljnice,
  PregledOdstotkov,
  RazdelitevSredstva,
  VnosRazdelitve,
  IzidUvozaRegistra,
  PregledRegistrov,
  SredstvoRegistra,
  NastavitveProjektov,
  PregledProjektov,
  Projekt,
  VnosProjekta,
  Obcutljivost,
  VrstaObdobjaObcutljivosti,
  Arhiv,
  DefinicijaKazalnika,
  FormulaKazalnika,
  IzbiraZavihka,
  MrezaGraditelja,
  PredogledFormule,
  DiagnostikaPosteZaPrikaz,
  DimenzijaZaPrikaz,
  DnevnikPoste,
  IzidPreizkusaPoste,
  StanjeUvozaZaPrikaz,
  UvozSprejet,
  IzidVabila,
  LetoPodatkov,
  UjemanjeOtvoritve,
  IzidBralnegaTesta,
  MinimaxPovezava,
  OrganizacijeMinimaxa,
  PovezavaPodjetjaZMinimaxom,
  ZahtevaUvozaPrekMinimaxa,
  OdgovorPorocila,
  OpozoriloUvoza,
  StanjeRazvrscanja,
  IzvirnaDatoteka,
  IzvirnaVrsticaKnjizbe,
  UskladitevOtvoritve,
  NastavitveFcVc,
  NastavitveKazalnikov,
  NastavitvePartnerjev,
  StranPrometa,
  OdstopanjeKazalnika,
  OdstopanjePravila,
  SplosneNastavitveKazalnikov,
  PredogledBrutoBilance,
  PregledNastavitev,
  PreslikavaPotrebna,
  PreslikavaStolpcev,
  PaketPodjetja,
  Podjetje,
  PorociloNaVoljo,
  PregledUporabnikov,
  Razclenitev,
  StanjeBaze,
  StanjeStrnitve,
  Strnitev,
  Uporabnik,
  VabiloZaPrikaz,
  VirFcVc,
  VrstaPorocila,
  Jezik,
  ProgramPoLetih,
} from '@koda/shared';

import { sporociSpremembaPodjetij } from './dogodki';

/** Zahteva je bila zavrnjena, ker uporabnik ni (vec) prijavljen. */
export class NiPrijavljen extends Error {}

/**
 * Kaj naredi vmesnik, ko seja potece sredi dela.
 *
 * Seja lahko potece ali jo admin preklice kadar koli, ne le ob nalaganju
 * strani. Namesto da bi vsaka stran to lovila sama, se obvestilo zgodi na
 * enem mestu - aplikacija nato pokaze prijavo.
 */
let obIzgubiSeje: () => void = () => {
  /* dokler aplikacija ne pove drugace, se ne zgodi nic */
};

export function nastaviObIzgubiSeje(kaj: () => void): void {
  obIzgubiSeje = kaj;
}

function izgubljenaSeja(): NiPrijavljen {
  obIzgubiSeje();
  return new NiPrijavljen('Prijava je potekla. Prijavi se znova.');
}

/**
 * Prebere odgovor kot JSON ali pove po clovesko, da ga ni.
 *
 * Kadar zahtevo prekine posrednik (predolgo delo, ponovni zagon storitve),
 * ne pride odgovor API, ampak stran HTML. Brez tega bi uporabnik videl
 * `Unexpected token '<' ... is not valid JSON`, kar mu ne pove nicesar.
 */
async function jsonAliRazlaga<T>(odgovor: Response): Promise<T> {
  const vrsta = odgovor.headers.get('content-type') ?? '';
  const besedilo = await odgovor.text();
  if (vrsta.includes('application/json')) {
    try {
      return JSON.parse(besedilo) as T;
    } catch {
      /* pade v razlago spodaj */
    }
  }
  throw new Error(
    odgovor.ok
      ? 'Strežnik ni vrnil pričakovanega odgovora. Poskusi znova; če se ponovi, javi skrbniku.'
      : `Strežnik je vrnil napako ${String(odgovor.status)} namesto odgovora. ` +
        'Zahteva je bila najverjetneje prekinjena; poskusi znova.',
  );
}

/**
 * Klic API.
 *
 * `sejaLahkoPotece` loci dvoje: pri obicajnem klicu 401 pomeni, da je seja
 * potekla in vmesnik pokaze prijavo, pri sami prijavi pa pomeni napacno geslo
 * - takrat mora uporabnik videti sporocilo streznika, ne "seja je potekla".
 */
/** Po uspešni spremembi podjetja obvesti glavni meni, da prebere sezname znova. */
function inSporoci<T>(obljuba: Promise<T>): Promise<T> {
  return obljuba.then((odgovor) => {
    sporociSpremembaPodjetij();
    return odgovor;
  });
}

async function preberi<T>(
  pot: string,
  moznosti?: RequestInit,
  sejaLahkoPotece = true,
): Promise<T> {
  const odgovor = await fetch(pot, moznosti);
  if (odgovor.status === 401 && sejaLahkoPotece) throw izgubljenaSeja();
  if (!odgovor.ok) {
    const telo = await jsonAliRazlaga<{ napaka?: string }>(odgovor).catch((e: unknown) => {
      throw e instanceof Error ? e : new Error(`Napaka ${String(odgovor.status)}.`);
    });
    throw new Error(telo.napaka ?? `Napaka ${String(odgovor.status)}.`);
  }
  return jsonAliRazlaga<T>(odgovor);
}

/** Telo zahteve v JSON. Zahteve, ki kaj spremenijo, gredo vedno skozi tole. */
/**
 * Izbira na zavihkih Partnerji in RVC po kupcih (docs/ARHITEKTURA.md, 6.5):
 * konti in stran prometa (samo Partnerji; brez njih veljajo privzete
 * prednastavitve podjetja) ter koliko partnerjev je prikazanih posebej.
 */
export interface ParametriPartnerjev {
  predpone?: string[] | undefined;
  stran?: StranPrometa | undefined;
  n?: number | undefined;
  /** RVC po kupcih: najslabši RVC najprej; brez tega najboljši. */
  vrstni?: 'najslabsi' | undefined;
}

/**
 * Isti parametri za prikaz in za izvoz v Excel, da se stevilke ne razidejo.
 * Predpone gredo kot ponovljen parameter (`predpone=22&predpone=23`).
 */
export function dodajParametrePartnerjev(q: URLSearchParams, p: ParametriPartnerjev): void {
  for (const k of p.predpone ?? []) q.append('predpone', k);
  if (p.stran !== undefined) q.set('stran', p.stran);
  if (p.n !== undefined) q.set('n', String(p.n));
  if (p.vrstni !== undefined) q.set('vrstni', p.vrstni);
}

const jsonTelo = (vsebina: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(vsebina),
});

export const api = {
  /** Prijavljeni uporabnik; vrže `NiPrijavljen`, kadar seje ni. */
  jaz: (): Promise<Uporabnik> => preberi('/api/jaz'),

  prijava: (email: string, geslo: string): Promise<{ prijavljen: boolean }> =>
    preberi('/api/prijava', jsonTelo({ email, geslo }), false),

  odjava: (): Promise<{ prijavljen: boolean }> => preberi('/api/odjava', jsonTelo({})),

  spremeniGeslo: (staro: string, novo: string): Promise<{ spremenjeno: boolean }> =>
    preberi('/api/geslo', jsonTelo({ staro, novo })),

  /** Shrani jezik pri uporabniku; velja na vsaki napravi. */
  nastaviJezik: (jezik: Jezik): Promise<{ jezik: Jezik }> =>
    preberi('/api/jezik', jsonTelo({ jezik })),

  /** Vabilo: pregled brez prijave, zato 401 tu ne pomeni potekle seje. */
  vabilo: (zeton: string): Promise<VabiloZaPrikaz> => preberi(`/api/vabilo/${zeton}`, undefined, false),

  /**
   * Sprejem vabila; jezik je tisti, v katerem je povabljeni bral vabilo, da
   * portal po prijavi ne preskoči v drug jezik.
   */
  sprejmiVabilo: (zeton: string, geslo: string, jezik: Jezik): Promise<{ prijavljen: boolean }> =>
    preberi(`/api/vabilo/${zeton}`, jsonTelo({ geslo, jezik }), false),

  /** Stran z novim geslom (povezava od skrbnika): brez prijave. */
  ponastavitev: (zeton: string): Promise<VabiloZaPrikaz> => preberi(`/api/ponastavitev/${zeton}`, undefined, false),

  /** Nastavi novo geslo po povezavi; druge seje uporabnika padejo, ta je prijavljena. */
  nastaviNovoGeslo: (zeton: string, geslo: string): Promise<{ prijavljen: boolean }> =>
    preberi(`/api/ponastavitev/${zeton}`, jsonTelo({ geslo }), false),

  uporabniki: (): Promise<PregledUporabnikov> => preberi('/api/uporabniki'),

  /** Dnevnik pošiljanj; odpre se, kadar nekaj ne dela. */
  dnevnikPoste: (moznosti?: { samoNeuspeli?: boolean; uporabnik?: string }): Promise<DnevnikPoste> => {
    const q = new URLSearchParams();
    if (moznosti?.samoNeuspeli) q.set('samoNeuspeli', '1');
    if (moznosti?.uporabnik) q.set('uporabnik', moznosti.uporabnik);
    const niz = q.toString();
    return preberi(`/api/posta/dnevnik${niz === '' ? '' : `?${niz}`}`);
  },

  /** Označi neuspelo pošiljanje kot obravnavano. */
  obravnavanoPosiljanje: (id: number): Promise<{ obravnavano: boolean }> =>
    preberi(`/api/posta/dnevnik/${String(id)}/obravnavano`, jsonTelo({})),

  /** Umakne podjetje v arhiv; podatki ostanejo. */
  arhivirajPodjetje: (podjetje: string): Promise<{ arhivirano: boolean }> =>
    inSporoci(preberi(`/api/podjetja/${podjetje}/arhiviraj`, jsonTelo({}))),

  obnoviPodjetje: (podjetje: string): Promise<{ obnovljeno: boolean }> =>
    inSporoci(preberi(`/api/podjetja/${podjetje}/obnovi`, jsonTelo({}))),

  /** Trajno izbriše podjetje iz arhiva; `naziv` je potrditev. */
  izbrisiPodjetje: (podjetje: string, naziv: string): Promise<{ izbrisano: boolean; knjizb: number }> =>
    inSporoci(
      preberi(`/api/podjetja/${podjetje}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ naziv }),
      }),
    ),

  arhivirajUporabnika: (uporabnik: string): Promise<{ arhivirano: boolean }> =>
    preberi(`/api/uporabniki/${uporabnik}/arhiviraj`, jsonTelo({})),

  obnoviUporabnika: (uporabnik: string): Promise<{ obnovljeno: boolean }> =>
    preberi(`/api/uporabniki/${uporabnik}/obnovi`, jsonTelo({})),

  /** Trajno izbriše uporabnika iz arhiva; `email` je potrditev. */
  izbrisiUporabnika: (uporabnik: string, email: string): Promise<{ izbrisano: boolean }> =>
    preberi(`/api/uporabniki/${uporabnik}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }),

  /** Kaj je umaknjeno iz uporabe. */
  arhiv: (): Promise<Arhiv> => preberi('/api/arhiv'),

  /** Koliko prostora zaseda baza; samo skrbnik. */
  stanjeBaze: (): Promise<StanjeBaze> => preberi('/api/stanje-baze'),

  /** Strnitev baze iz portala (samo skrbnik; docs/ARHITEKTURA.md, 5.6). */
  strnitev: (): Promise<StanjeStrnitve> => preberi('/api/strnitev'),
  nacrtujStrnitev: (vnos: { tabele: string[]; ob: string | null; geslo: string }): Promise<Strnitev> =>
    preberi('/api/strnitev', jsonTelo(vnos)),
  prekliciStrnitev: (id: string): Promise<unknown> =>
    preberi(`/api/strnitev/${id}`, { ...jsonTelo({}), method: 'DELETE' }),

  /** Kaj je pri pošti nastavljeno in kaj o tem pravi Microsoft. */
  diagnostikaPoste: (): Promise<DiagnostikaPosteZaPrikaz> => preberi('/api/posta/diagnostika'),

  /** Poskusno sporočilo; brez naslova gre na naslov prijavljenega skrbnika. */
  preizkusPoste: (za?: string): Promise<IzidPreizkusaPoste> =>
    preberi('/api/posta/preizkus', jsonTelo({ za })),

  povabi: (
    email: string,
    ime: string,
    podjetja: { id: string; vloga: string }[],
  ): Promise<IzidVabila> => preberi('/api/uporabniki/vabilo', jsonTelo({ email, ime, podjetja })),

  /** Dodeli podjetje obstoječemu uporabniku, brez novega vabila. */
  dodeliPodjetje: (uporabnik: string, podjetje: string): Promise<{ dodeljeno: boolean }> =>
    preberi(`/api/uporabniki/${uporabnik}/podjetja/${podjetje}`, {
      ...jsonTelo({ vloga: 'viewer' }),
      method: 'PUT',
    }),

  /** Odvzame podjetje uporabniku; velja takoj. */
  odvzemiPodjetje: (uporabnik: string, podjetje: string): Promise<{ odvzeto: boolean }> =>
    preberi(`/api/uporabniki/${uporabnik}/podjetja/${podjetje}`, {
      ...jsonTelo({}),
      method: 'DELETE',
    }),

  /** Pošlje uporabniku povezavo za novo geslo (samo skrbnik). */
  ponastaviGeslo: (uporabnik: string): Promise<IzidVabila> =>
    preberi(`/api/uporabniki/${uporabnik}/ponastavi-geslo`, jsonTelo({})),

  koncajVseSeje: (uporabnik: string): Promise<{ koncanihSej: number }> =>
    preberi(`/api/uporabniki/${uporabnik}/preklici-seje`, jsonTelo({})),

  podjetja: (): Promise<Podjetje[]> => preberi('/api/podjetja'),

  ustvariPodjetje: (naziv: string, vir: string): Promise<Podjetje> =>
    inSporoci(preberi('/api/podjetja', jsonTelo({ naziv, vir }))),

  /** Preimenuje podjetje; vir se ne spreminja, ker ga dolocajo knjizbe. */
  preimenujPodjetje: (podjetje: string, naziv: string): Promise<Podjetje> =>
    inSporoci(
      preberi(`/api/podjetja/${podjetje}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ naziv }),
      }),
    ),

  leta: (podjetje: string): Promise<LetoPodatkov[]> =>
    preberi(`/api/podjetja/${podjetje}/leta`),

  /** Ujemanje otvoritve leta s stanjem 31. 12. preteklega leta (5.5); samo skrbnik. */
  ujemanjeOtvoritve: (podjetje: string, leto: number): Promise<UjemanjeOtvoritve> =>
    preberi(`/api/podjetja/${podjetje}/leta/${String(leto)}/ujemanje-otvoritve`),

  /** Skrbnik popravi pokritost uvoza leta: do katerega meseca so podatki popolni (0 = samo otvoritev). */
  popraviPokritost: (podjetje: string, leto: number, do_: number): Promise<{ v_redu: boolean }> =>
    preberi(`/api/podjetja/${podjetje}/leta/${String(leto)}/pokritost`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ do: do_ }),
    }),

  /** Poročila, ki jih podjetje ima glede na paket; nazivi v izbranem jeziku. */
  porocilaNaVoljo: (podjetje: string, jezik?: Jezik): Promise<PorociloNaVoljo[]> =>
    preberi(
      `/api/podjetja/${podjetje}/porocila${jezik === undefined ? '' : `?jezik=${jezik}`}`,
    ),

  /** Šifre modulov, ki so pri podjetju vklopljeni (npr. `izvoz-excel`). */
  moduli: (podjetje: string): Promise<string[]> => preberi(`/api/podjetja/${podjetje}/moduli`),

  paket: (podjetje: string): Promise<PaketPodjetja> => preberi(`/api/podjetja/${podjetje}/paket`),

  nastaviPaket: (
    podjetje: string,
    paket: string | null,
    moduli: Record<string, boolean>,
  ): Promise<PaketPodjetja> =>
    // Paket in moduli določajo zavihke poročil v glavnem meniju.
    inSporoci(
      preberi(`/api/podjetja/${podjetje}/paket`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paket, moduli }),
      }),
    ),

  porocilo: (
    podjetje: string,
    vrsta: VrstaPorocila,
    leto: number,
    dimenzija?: { koda: string; vrednosti: string[]; brez: boolean },
    jezik?: Jezik,
    primerjalno?: number,
    obdobje?: { od: number; do: number },
    partnerji?: ParametriPartnerjev,
  ): Promise<OdgovorPorocila> => {
    const q = new URLSearchParams({ leto: String(leto) });
    // Razpon mesecev velja pri Primerjavi; drugod ga API prezre.
    if (obdobje !== undefined) {
      q.set('obdobjeOd', String(obdobje.od));
      q.set('obdobjeDo', String(obdobje.do));
    }
    if (partnerji !== undefined) dodajParametrePartnerjev(q, partnerji);
    if (dimenzija) {
      q.set('dimenzija', dimenzija.koda);
      for (const v of dimenzija.vrednosti) q.append('vrednosti', v);
      if (dimenzija.brez) q.set('brezDimenzije', 'true');
    }
    // Primerjalno leto velja pri Primerjavi in Kazalnikih; drugod ga API
    // prezre. Brez njega strežnik vzame zadnje starejše leto s knjižbami, in
    // kadar takega ni, poročilo nima primerjalnega leta.
    if (primerjalno !== undefined) q.set('primerjalno', String(primerjalno));
    // Jezik gre zraven, da se porocilo ob preklopu prebere znova; brez njega
    // bi na zaslonu ostali stari nazivi do osvezitve strani.
    if (jezik !== undefined) q.set('jezik', jezik);
    return preberi(`/api/podjetja/${podjetje}/porocila/${vrsta}?${q.toString()}`);
  },

  /**
   * Zavihek Občutljivost strani Kazalniki: točka preloma in matrika EBITDA
   * za obdobje (`celo`, `tekoce`) in strošek (2- ali 3-mestni konto).
   * Brez izbire velja privzeto (zadnje celo leto, strošek z največjim
   * variabilnim zneskom).
   */
  obcutljivost: (
    podjetje: string,
    jezik?: Jezik,
    obdobje?: VrstaObdobjaObcutljivosti,
    strosek?: string,
  ): Promise<Obcutljivost> => {
    const q = new URLSearchParams();
    if (obdobje !== undefined) q.set('obdobje', obdobje);
    if (strosek !== undefined) q.set('strosek', strosek);
    if (jezik !== undefined) q.set('jezik', jezik);
    return preberi(`/api/podjetja/${podjetje}/porocila/kazalniki/obcutljivost?${q.toString()}`);
  },

  /**
   * Dimenzije za izbirnik nad porocilom.
   *
   * Vrsta porocila gre zraven, ker se stevila in zneski nanasajo na to, kar
   * uporabnik gleda - ne na vse knjizbe podjetja (docs/ARHITEKTURA.md, 6.4).
   */
  dimenzije: (
    podjetje: string,
    leto: number,
    porocilo: VrstaPorocila,
    jezik?: Jezik,
    primerjalno?: number,
    obdobje?: { od: number; do: number },
  ): Promise<DimenzijaZaPrikaz[]> => {
    const q = new URLSearchParams({ leto: String(leto), porocilo });
    if (obdobje !== undefined) {
      q.set('obdobjeOd', String(obdobje.od));
      q.set('obdobjeDo', String(obdobje.do));
    }
    if (jezik !== undefined) q.set('jezik', jezik);
    // Pri Primerjavi in Kazalnikih izbirnik zajame obe leti, zato mora vedeti,
    // katero je primerjalno; drugod API ta parameter prezre.
    if (primerjalno !== undefined) q.set('primerjalno', String(primerjalno));
    return preberi(`/api/podjetja/${podjetje}/dimenzije?${q.toString()}`);
  },

  razclenitev: (
    podjetje: string,
    parametri: [string, string][],
    jezik?: Jezik,
  ): Promise<Razclenitev> => {
    const q = new URLSearchParams(parametri);
    if (jezik !== undefined) q.set('jezik', jezik);
    return preberi(`/api/podjetja/${podjetje}/razclenitev?${q.toString()}`);
  },

  /**
   * Naloži izvoz glavne knjige (CSV ali Excel).
   *
   * Odgovor 422 pomeni zavrnjen uvoz z napakami, 409 pa da ima datoteka več
   * zavihkov z glavno knjigo in je treba izbrati pravega.
   */
  uvoz: async (
    podjetje: string,
    leto: number,
    vir: string,
    datoteka: File,
    zavihek?: string,
  ): Promise<UvozSprejet | IzbiraZavihka | { preslikavaPotrebna: PreslikavaPotrebna }> => {
    const jeExcel = /\.xlsx?$/i.test(datoteka.name);
    // Ime gre zraven zaradi arhiva izvirnikov (docs/ARHITEKTURA.md, 5.6).
    const q = new URLSearchParams({ leto: String(leto), vir, ime: datoteka.name });
    if (zavihek !== undefined) q.set('zavihek', zavihek);

    const odgovor = await fetch(`/api/podjetja/${podjetje}/uvoz?${q.toString()}`, {
      method: 'POST',
      headers: {
        'Content-Type': jeExcel
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv',
      },
      // Datoteka gre naravnost v telo zahteve; brskalnik jo pošlje po kosih,
      // zato je tudi pri velikem izvozu ne beremo v pomnilnik.
      body: datoteka,
    });
    if (odgovor.status === 401) throw izgubljenaSeja();
    const telo = await jsonAliRazlaga<
      UvozSprejet & {
        napaka?: string;
        zavihki?: string[];
        preslikavaPotrebna?: PreslikavaPotrebna;
      }
    >(odgovor);
    if (odgovor.status === 409 && telo.zavihki) {
      return { zavihki: telo.zavihki, sporocilo: telo.napaka ?? 'Izberi zavihek.' };
    }
    // Obveznega stolpca ni mogoče prepoznati: namesto napake dobi vmesnik
    // stolpce datoteke in ponudi preslikavo (docs/ARHITEKTURA.md, 5.2).
    if (odgovor.status === 422 && telo.preslikavaPotrebna) {
      return { preslikavaPotrebna: telo.preslikavaPotrebna };
    }
    if (!odgovor.ok) throw new Error(telo.napaka ?? `Napaka ${String(odgovor.status)}.`);
    return telo;
  },

  /** Predogled bruto bilance: kaj portal prebere iz glave, preden se naloži (5.4). */
  predogledOtvoritve: async (podjetje: string, datoteka: File): Promise<PredogledBrutoBilance> => {
    const odgovor = await fetch(`/api/podjetja/${podjetje}/otvoritev/predogled`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      body: datoteka,
    });
    if (odgovor.status === 401) throw izgubljenaSeja();
    const telo = await jsonAliRazlaga<PredogledBrutoBilance & { napaka?: string }>(odgovor);
    if (!odgovor.ok) throw new Error(telo.napaka ?? `Napaka ${String(odgovor.status)}.`);
    return telo;
  },

  /**
   * Naloži otvoritev iz bruto bilance.
   *
   * Samo za vire, ki otvoritve ne izvazajo (Business Central). Bruto bilanca
   * ima nekaj sto vrstic, zato odgovor pride takoj in obdelave v ozadju ni.
   */
  otvoritev: async (
    podjetje: string,
    leto: number,
    datoteka: File,
    /** Skrbnik potrjuje leto, ceprav ga iz glave datoteke ni mogoce preveriti. */
    potrdiNepreverjeno = false,
  ): Promise<{ kontov: number; potrjen: boolean; opozorila: OpozoriloUvoza[] }> => {
    const odgovor = await fetch(
      `/api/podjetja/${podjetje}/otvoritev?${new URLSearchParams({
        leto: String(leto),
        ime: datoteka.name,
        ...(potrdiNepreverjeno ? { potrdiNepreverjeno: '1' } : {}),
      }).toString()}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        body: datoteka,
      },
    );
    const telo = await jsonAliRazlaga<{
      kontov?: number;
      potrjen?: boolean;
      opozorila?: OpozoriloUvoza[];
      napaka?: string;
    }>(odgovor);
    if (!odgovor.ok && odgovor.status !== 422) {
      throw new Error(telo.napaka ?? `Napaka ${String(odgovor.status)}.`);
    }
    return {
      kontov: telo.kontov ?? 0,
      potrjen: telo.potrjen ?? false,
      opozorila: telo.opozorila ?? (telo.napaka === undefined ? [] : [{ vrsta: 'napaka', sporocilo: telo.napaka, napaka: true }]),
    };
  },

  /**
   * Izracuna otvoritev iz preteklega leta (docs/ARHITEKTURA.md, 5.4).
   *
   * Otvoritev leta N = otvoritev leta N-1 + promet leta N-1; izid preteklega
   * leta gre na nastavljen konto kapitala.
   */
  izracunajOtvoritev: async (
    podjetje: string,
    leto: number,
  ): Promise<{
    kontov: number;
    potrjen: boolean;
    opozorila: OpozoriloUvoza[];
    izid?: string;
    kontoIzida?: string;
    izLeta?: number;
  }> => {
    const odgovor = await fetch(
      `/api/podjetja/${podjetje}/otvoritev/izracun?leto=${String(leto)}`,
      { ...jsonTelo({}) },
    );
    const telo = await jsonAliRazlaga<{
      kontov?: number;
      potrjen?: boolean;
      opozorila?: OpozoriloUvoza[];
      izid?: string;
      kontoIzida?: string;
      izLeta?: number;
      napaka?: string;
    }>(odgovor);
    if (!odgovor.ok && odgovor.status !== 422) {
      throw new Error(telo.napaka ?? `Napaka ${String(odgovor.status)}.`);
    }
    return {
      kontov: telo.kontov ?? 0,
      potrjen: telo.potrjen ?? false,
      opozorila:
        telo.opozorila ??
        (telo.napaka === undefined
          ? []
          : [{ vrsta: 'napaka', sporocilo: telo.napaka, napaka: true }]),
      ...(telo.izid === undefined ? {} : { izid: telo.izid }),
      ...(telo.kontoIzida === undefined ? {} : { kontoIzida: telo.kontoIzida }),
      ...(telo.izLeta === undefined ? {} : { izLeta: telo.izLeta }),
    };
  },

  /** Nastavitve razvrscanja, kot veljajo za to podjetje. */
  nastavitve: (podjetje: string): Promise<PregledNastavitev> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve`),

  shraniSkupino: (
    podjetje: string,
    skupina: string,
    razpon: { od: number; do: number },
  ): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/skupine/${skupina}`, {
      ...jsonTelo(razpon),
      method: 'PUT',
    }),

  ponastaviSkupino: (podjetje: string, skupina: string): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/skupine/${skupina}`, {
      ...jsonTelo({}),
      method: 'DELETE',
    }),

  dodajSkupino: (
    podjetje: string,
    skupina: { naziv: string; nadskupina: string; od: number; do: number },
  ): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/skupine`, jsonTelo(skupina)),

  shraniPostavko: (podjetje: string, postavka: string, konti: string[]): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/postavke/${postavka}`, {
      ...jsonTelo({ konti }),
      method: 'PUT',
    }),

  ponastaviPostavko: (podjetje: string, postavka: string): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/postavke/${postavka}`, {
      ...jsonTelo({}),
      method: 'DELETE',
    }),

  dodajPostavko: (
    podjetje: string,
    postavka: { naziv: string; nadrejena: string; konti: string[] },
  ): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/postavke`, jsonTelo(postavka)),

  shraniPostavkoCf: (podjetje: string, postavka: string, konti: string[]): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/cf/${postavka}`, {
      ...jsonTelo({ konti }),
      method: 'PUT',
    }),

  ponastaviPostavkoCf: (podjetje: string, postavka: string): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/cf/${postavka}`, {
      ...jsonTelo({}),
      method: 'DELETE',
    }),

  dodajPostavkoCf: (
    podjetje: string,
    postavka: { naziv: string; nadrejena: string; konti: string[] },
  ): Promise<unknown> => preberi(`/api/podjetja/${podjetje}/nastavitve/cf`, jsonTelo(postavka)),

  /** Konto, na katerega gre ob letnem prehodu izid preteklega leta. */
  shraniPrenosIzida: (podjetje: string, konto: string): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/prenos-izida`, {
      ...jsonTelo({ konto }),
      method: 'PUT',
    }),

  /** Nastavitve kazalnikov podjetja (docs/ARHITEKTURA.md, 6.3). */
  nastavitveKazalnikov: (podjetje: string): Promise<NastavitveKazalnikov> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/kazalniki`),

  /** Nastavitve FC/VC podjetja (samo skrbnik). */
  nastavitveFcVc: (podjetje: string): Promise<NastavitveFcVc> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/fcvc`),

  /** Variabilni delež konta (0–1 po 0,05); nastavitev istega konta se prepiše. */
  shraniDelezFcVc: (podjetje: string, konto: string, delez: string, vir: VirFcVc = 'manual'): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/fcvc/${konto}`, { ...jsonTelo({ delez, vir }), method: 'PUT' }),

  /** Odstrani nastavitev konta; 3-mestni konto spet deduje od 2-mestnega. */
  odstraniDelezFcVc: (podjetje: string, konto: string): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/fcvc/${konto}`, { ...jsonTelo({}), method: 'DELETE' }),

  /** Konti razlike v ceni; zamenja oba seznama. */
  shraniKonteRazlikeVCeni: (
    podjetje: string,
    konti: { prodajna: string[]; nabavna: string[] },
  ): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/kazalniki/razlika-v-ceni`, {
      ...jsonTelo(konti),
      method: 'PUT',
    }),

  /** Vloga vrstice, ki jo je dodalo podjetje; `null` vlogo odvzame. */
  shraniVlogoVrstice: (
    podjetje: string,
    vnos: { raven: string; id: string; vloga: string | null },
  ): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/kazalniki/vloga`, { ...jsonTelo(vnos), method: 'PUT' }),

  /** Nastavitev kazalnika pri podjetju; `null` ponastavi na privzeto. */
  shraniVklopKazalnika: (podjetje: string, koda: string, vklopljen: boolean | null): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/kazalniki/vklop`, {
      ...jsonTelo({ koda, vklopljen }),
      method: 'PUT',
    }),

  /**
   * Mreža graditelja kazalnikov za podjetje (samo skrbnik): vrstice sheme z
   * vrednostmi v stolpcih A, B in C. Brez leta strežnik vzame najnovejše.
   */
  graditelj: (podjetje: string, leto?: number, primerjalno?: number): Promise<MrezaGraditelja> => {
    const q = new URLSearchParams();
    if (leto !== undefined) q.set('leto', String(leto));
    if (primerjalno !== undefined) q.set('primerjalno', String(primerjalno));
    return preberi(`/api/podjetja/${podjetje}/graditelj?${q.toString()}`);
  },

  /** Predogled formule pri podjetju (nič se ne shrani); 400 z razlogom, če formula ni veljavna. */
  predogledFormule: (
    podjetje: string,
    formula: FormulaKazalnika,
    leto: number,
    primerjalno: number,
  ): Promise<PredogledFormule> =>
    preberi(`/api/podjetja/${podjetje}/graditelj/predogled`, jsonTelo({ formula, leto, primerjalno })),

  /** Splošne nastavitve kazalnikov (samo skrbnik). */
  splosneNastavitveKazalnikov: (): Promise<SplosneNastavitveKazalnikov> => preberi('/api/nastavitve/kazalniki'),

  /** Odstopanje kazalnika; polja `null` pomenijo privzeto. */
  shraniSplosniKazalnik: (koda: string, odstopanje: OdstopanjeKazalnika): Promise<unknown> =>
    preberi(`/api/nastavitve/kazalniki/kazalniki/${encodeURIComponent(koda)}`, {
      ...jsonTelo(odstopanje),
      method: 'PUT',
    }),

  ponastaviSplosniKazalnik: (koda: string): Promise<unknown> =>
    preberi(`/api/nastavitve/kazalniki/kazalniki/${encodeURIComponent(koda)}`, { method: 'DELETE' }),

  /**
   * Kazalnik iz graditelja: nov (`koda === null`, koda je v telesu) ali
   * sprememba obstoječega. Vrne opozorila formule; 400 z razlogom, če formula
   * ali podatki niso veljavni.
   */
  shraniDefinicijo: (koda: string | null, definicija: DefinicijaKazalnika): Promise<{ koda: string; opozorila: string[] }> =>
    preberi(
      koda === null ? '/api/nastavitve/kazalniki/definicije' : `/api/nastavitve/kazalniki/definicije/${encodeURIComponent(koda)}`,
      { ...jsonTelo(definicija), method: koda === null ? 'POST' : 'PUT' },
    ),

  /** Izbris kazalnika iz graditelja (skupaj z njegovimi nastavitvami). */
  izbrisiDefinicijo: (koda: string): Promise<unknown> =>
    preberi(`/api/nastavitve/kazalniki/definicije/${encodeURIComponent(koda)}`, { method: 'DELETE' }),

  /** Odstopanje pravila opozoril; polja `null` pomenijo privzeto. */
  shraniSplosnoPravilo: (koda: string, odstopanje: OdstopanjePravila): Promise<unknown> =>
    preberi(`/api/nastavitve/kazalniki/pravila/${encodeURIComponent(koda)}`, {
      ...jsonTelo(odstopanje),
      method: 'PUT',
    }),

  ponastaviSplosnoPravilo: (koda: string): Promise<unknown> =>
    preberi(`/api/nastavitve/kazalniki/pravila/${encodeURIComponent(koda)}`, { method: 'DELETE' }),

  /** Vloga vrste temeljnice: navadna, otvoritev ali zapiranje (5.9). */
  shraniVlogoTemeljnice: (podjetje: string, vrsta: string, vloga: string): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/vrste-temeljnic`, {
      ...jsonTelo({ vrsta, vloga }),
      method: 'PUT',
    }),

  /**
   * Leta, pri katerih obdobja knjizb ne ustrezajo vlogi otvoritve (5.3).
   * Samo branje.
   */
  uskladitevOtvoritve: (podjetje: string): Promise<UskladitevOtvoritve> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/vrste-temeljnic/uskladitev`),

  /**
   * Uveljavi vlogo otvoritve na uvozenih letih. Loceno od shranjevanja vloge:
   * skrbnik mora vedeti, kdaj se stevilke v porocilih premaknejo.
   */
  uskladiOtvoritev: (podjetje: string): Promise<UskladitevOtvoritve> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/vrste-temeljnic/uskladitev`, jsonTelo({})),

  ponastaviPrenosIzida: (podjetje: string): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/prenos-izida`, {
      ...jsonTelo({}),
      method: 'DELETE',
    }),

  /**
   * Nastavitve zavihkov Partnerji in RVC po kupcih (docs/ARHITEKTURA.md, 6.5).
   * Bere jih vsak uporabnik podjetja; `smemUrejati` pove, ali jih sme spreminjati.
   */
  nastavitvePartnerjev: (podjetje: string): Promise<NastavitvePartnerjev> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/partnerji`),

  /** Zamenja vse prednastavitve podjetja; prazen seznam vrne privzete iz standardne sheme. */
  shraniPrednastavitvePartnerjev: (
    podjetje: string,
    prednastavitve: { naziv: string; predpone: string[]; stran: StranPrometa; privzeta: boolean }[],
  ): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/partnerji`, { ...jsonTelo({ prednastavitve }), method: 'PUT' }),

  /** Vrste dokumentov, ki stejejo v RVC po kupcih; prazen seznam pomeni vse. */
  shraniVrsteRvc: (podjetje: string, vrste: string[]): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/partnerji/vrste-rvc`, { ...jsonTelo({ vrste }), method: 'PUT' }),

  /** Odstrani vse prilagoditve podjetja; ostane standardna shema. */
  ponastaviVse: (podjetje: string): Promise<unknown> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve`, { ...jsonTelo({}), method: 'DELETE' }),

  /**
   * Ponovno razvrsti knjizbe podjetja.
   *
   * Loceno od shranjevanja pravila: skrbnik mora vedeti, kdaj se stevilke v
   * porocilih premaknejo (docs/ARHITEKTURA.md, 4.6).
   */
  prerazvrsti: (podjetje: string): Promise<StanjeRazvrscanja> =>
    preberi(`/api/podjetja/${podjetje}/prerazvrsti`, jsonTelo({})),
  /** Stanje razvrscanja v ozadju; vmesnik poizveduje, dokler je v teku. */
  stanjeRazvrscanja: (podjetje: string): Promise<StanjeRazvrscanja> =>
    preberi(`/api/podjetja/${podjetje}/prerazvrsti`),

  /** Izvirne datoteke uvozov podjetja (samo skrbnik, docs/ARHITEKTURA.md, 5.6). */
  izvirniki: (podjetje: string): Promise<IzvirnaDatoteka[]> => preberi(`/api/podjetja/${podjetje}/izvirniki`),

  /** Povezava podjetja z Minimaxom prek API (samo skrbnik; docs/ARHITEKTURA.md, 5.10). */
  minimaxPovezava: (podjetje: string): Promise<PovezavaPodjetjaZMinimaxom> => preberi(`/api/podjetja/${podjetje}/minimax`),
  minimaxOrganizacije: (podjetje: string): Promise<OrganizacijeMinimaxa> =>
    preberi(`/api/podjetja/${podjetje}/minimax/organizacije`),
  shraniMinimaxPovezavo: (podjetje: string, telo: { organizacijaId: string; naziv: string }): Promise<MinimaxPovezava> =>
    preberi(`/api/podjetja/${podjetje}/minimax`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(telo),
    }),
  odstraniMinimaxPovezavo: (podjetje: string): Promise<{ v_redu: boolean }> =>
    preberi(`/api/podjetja/${podjetje}/minimax`, { method: 'DELETE' }),
  /** Uvoz knjižb prek Minimax API; obdelava teče v ozadju, stanje pove `stanjeUvoza`. */
  minimaxUvoz: (podjetje: string, zahteva: ZahtevaUvozaPrekMinimaxa): Promise<UvozSprejet> =>
    preberi(`/api/podjetja/${podjetje}/minimax/uvoz`, jsonTelo(zahteva)),
  minimaxBralniTest: (podjetje: string, leto: number): Promise<IzidBralnegaTesta> =>
    preberi(`/api/podjetja/${podjetje}/minimax/bralni-test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ leto }),
    }),

  /** Naslov za prenos izvirnika; brskalnik ga odpre s sejo kot vsako drugo zahtevo. */
  naslovIzvirnika: (podjetje: string, datoteka: string): string => `/api/podjetja/${podjetje}/izvirniki/${datoteka}`,
  /** Izvirna vrstica knjižbe iz datoteke uvoza (samo skrbnik). */
  izvirnaVrstica: (podjetje: string, knjizba: number): Promise<IzvirnaVrsticaKnjizbe> =>
    preberi(`/api/podjetja/${podjetje}/knjizbe/${String(knjizba)}/izvirna-vrstica`),

  /** Kje je uvoz; vmesnik to poizveduje, dokler obdelava tece. */
  stanjeUvoza: (podjetje: string, uvoz: string): Promise<StanjeUvozaZaPrikaz> =>
    preberi(`/api/podjetja/${podjetje}/uvoz/${uvoz}`),

  /** Katera polja vir pozna in katera preslikava velja pri podjetju. */
  /** Preslikava stolpcev za vir, ki pri podjetju velja za `leto` (5.2); brez leta za zadnje obdobje. */
  preslikavaStolpcev: (podjetje: string, leto?: number): Promise<PreslikavaStolpcev> =>
    preberi(`/api/podjetja/${podjetje}/preslikava-stolpcev${leto === undefined ? '' : `?leto=${String(leto)}`}`),

  /** Program (vir) po letih (docs/ARHITEKTURA.md, 5.2); samo skrbnik. */
  programPoLetih: (podjetje: string): Promise<ProgramPoLetih> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/vir-po-letih`),

  /** Doda prehod: od leta naprej velja drug program; z `doLeta` samo do tega leta (potem spet prejšnji). */
  dodajPrehodPrograma: (podjetje: string, odLeta: number, vir: string, doLeta: number | null = null): Promise<ProgramPoLetih> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/vir-po-letih`, jsonTelo({ odLeta, doLeta, vir })),

  /** Odstrani prehod; mogoče, dokler od tega leta ni uvoza iz tega programa. */
  odstraniPrehodPrograma: (podjetje: string, odLeta: number): Promise<ProgramPoLetih> =>
    preberi(`/api/podjetja/${podjetje}/nastavitve/vir-po-letih/${String(odLeta)}`, { method: 'DELETE' }),

  /** Modul Projekti, 1. del (docs/ARHITEKTURA.md, 15): sifrant projektov in nastavitve. */
  projekti: (podjetje: string): Promise<PregledProjektov> => preberi(`/api/podjetja/${podjetje}/projekti`),

  /** Modul Projekti, 6. del (15.6): zakljucki mesecev; samo admin. */
  zakljucki: (podjetje: string): Promise<PregledZakljuckov> => preberi(`/api/podjetja/${podjetje}/projekti/zakljucki`),
  zakljuciMesec: (podjetje: string, mesec: string): Promise<ZakljucenMesec> =>
    preberi(`/api/podjetja/${podjetje}/projekti/zakljucki`, jsonTelo({ mesec })),
  odpriMesec: (podjetje: string, mesec: string): Promise<{ odprt: boolean }> =>
    preberi(`/api/podjetja/${podjetje}/projekti/zakljucki/${mesec}`, { method: 'DELETE' }),
  naslovZakljucka: (podjetje: string, mesec: string): string => `/api/podjetja/${podjetje}/projekti/zakljucki/${mesec}/datoteka`,
  /** Modul Projekti, 5. del (15.5): pregledi za stranko. */
  projektiPregled: (podjetje: string): Promise<PregledPorabeProjektov> => preberi(`/api/podjetja/${podjetje}/projekti/pregled`),
  projektiPoraba: (podjetje: string, leto?: number): Promise<PorabaProjektov> =>
    preberi(`/api/podjetja/${podjetje}/projekti/poraba${leto === undefined ? '' : `?leto=${String(leto)}`}`),
  /** Knjižbe celice Porabe; projekt `null` = brez projekta. */
  projektiPorabaKnjizbe: (podjetje: string, leto: number, mesec: number, projekt: string | null): Promise<KnjizbaDelitve[]> =>
    preberi(
      `/api/podjetja/${podjetje}/projekti/poraba/knjizbe?${new URLSearchParams({ leto: String(leto), mesec: String(mesec), projekt: projekt ?? 'brez' }).toString()}`,
    ),
  projektiPoProjektih: (podjetje: string, projekt?: string, mesec?: string): Promise<ProjektPoMesecu> => {
    const q = new URLSearchParams();
    if (projekt !== undefined) q.set('projekt', projekt);
    if (mesec !== undefined) q.set('mesec', mesec);
    const niz = q.toString();
    return preberi(`/api/podjetja/${podjetje}/projekti/po-projektih${niz === '' ? '' : `?${niz}`}`);
  },
  /** Modul Projekti, 4. del (15.4): stanje delitve amortizacije meseca in vrstice temeljnice; samo admin. */
  temeljnicaAmortizacije: (podjetje: string, mesec?: string): Promise<PregledTemeljnice> =>
    preberi(`/api/podjetja/${podjetje}/projekti/temeljnica${mesec === undefined ? '' : `?mesec=${mesec}`}`),
  /** Naslov za prenos temeljnice (.xlsx) za uvoz v program. */
  naslovTemeljnice: (podjetje: string, mesec: string): string => `/api/podjetja/${podjetje}/projekti/temeljnica/datoteka?mesec=${mesec}`,
  /** Modul Projekti, 3. del (15.3): sredstva z razdelitvijo na projekte, ki velja v mesecu (`llll-mm`). */
  odstotkiProjektov: (podjetje: string, mesec?: string): Promise<PregledOdstotkov> =>
    preberi(`/api/podjetja/${podjetje}/projekti/odstotki${mesec === undefined ? '' : `?mesec=${mesec}`}`),
  zgodovinaOdstotkov: (podjetje: string, inventarna: string): Promise<RazdelitevSredstva[]> =>
    preberi(`/api/podjetja/${podjetje}/projekti/odstotki/${encodeURIComponent(inventarna)}`),
  shraniOdstotke: (podjetje: string, inventarna: string, vnos: VnosRazdelitve): Promise<RazdelitevSredstva> =>
    preberi(`/api/podjetja/${podjetje}/projekti/odstotki/${encodeURIComponent(inventarna)}`, { ...jsonTelo(vnos), method: 'PUT' }),
  odstraniOdstotke: (podjetje: string, inventarna: string, veljaOd: string): Promise<{ odstranjeno: boolean }> =>
    preberi(`/api/podjetja/${podjetje}/projekti/odstotki/${encodeURIComponent(inventarna)}/${veljaOd}`, { method: 'DELETE' }),
  /** Modul Projekti, 2. del (15.2): veljavni registri osnovnih sredstev z vsotami in amortizacijske skupine. */
  registri: (podjetje: string): Promise<PregledRegistrov> => preberi(`/api/podjetja/${podjetje}/projekti/register`),
  /** Sredstva enega registra (razclenitev vsot). */
  sredstvaRegistra: (podjetje: string, uvoz: string): Promise<SredstvoRegistra[]> =>
    preberi(`/api/podjetja/${podjetje}/projekti/register/${uvoz}`),
  umakniRegister: (podjetje: string, uvoz: string): Promise<{ umaknjen: boolean }> =>
    preberi(`/api/podjetja/${podjetje}/projekti/register/${uvoz}`, { method: 'DELETE' }),
  shraniAmortizacijskeSkupine: (podjetje: string, skupine: AmortizacijskaSkupina[]): Promise<AmortizacijskaSkupina[]> =>
    preberi(`/api/podjetja/${podjetje}/projekti/amortizacijske-skupine`, { ...jsonTelo({ skupine }), method: 'PUT' }),
  /**
   * Nalozi izvoz registra za leto in mesec. Pri vec zavihkih z registrom
   * vrne seznam zavihkov; uporabnik izbere in datoteka gre se enkrat.
   */
  naloziRegister: async (
    podjetje: string,
    leto: number,
    mesec: number,
    datoteka: File,
    zavihek?: string,
  ): Promise<IzidUvozaRegistra | IzbiraZavihka> => {
    const q = new URLSearchParams({ leto: String(leto), mesec: String(mesec), ime: datoteka.name });
    if (zavihek !== undefined) q.set('zavihek', zavihek);
    const odgovor = await fetch(`/api/podjetja/${podjetje}/projekti/register?${q.toString()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      body: datoteka,
    });
    if (odgovor.status === 401) throw izgubljenaSeja();
    const telo = await jsonAliRazlaga<Partial<IzidUvozaRegistra> & { napaka?: string; zavihki?: string[] }>(odgovor);
    if (odgovor.status === 409 && telo.zavihki) return { zavihki: telo.zavihki, sporocilo: telo.napaka ?? 'Izberi zavihek.' };
    if (!odgovor.ok && odgovor.status !== 422) throw new Error(telo.napaka ?? `Napaka ${String(odgovor.status)}.`);
    return {
      potrjen: telo.potrjen ?? false,
      sredstev: telo.sredstev ?? 0,
      preskocenih: telo.preskocenih ?? 0,
      opozorila: telo.opozorila ?? (telo.napaka === undefined ? [] : [{ vrsta: 'napaka', sporocilo: telo.napaka, napaka: true }]),
    };
  },
  dodajProjekt: (podjetje: string, vnos: VnosProjekta): Promise<Projekt> =>
    preberi(`/api/podjetja/${podjetje}/projekti`, jsonTelo(vnos)),
  spremeniProjekt: (podjetje: string, projekt: string, vnos: VnosProjekta): Promise<Projekt> =>
    preberi(`/api/podjetja/${podjetje}/projekti/${projekt}`, { ...jsonTelo(vnos), method: 'PUT' }),
  odstraniProjekt: (podjetje: string, projekt: string): Promise<{ odstranjeno: boolean }> =>
    preberi(`/api/podjetja/${podjetje}/projekti/${projekt}`, { method: 'DELETE' }),
  shraniNastavitveProjektov: (podjetje: string, nastavitve: NastavitveProjektov): Promise<NastavitveProjektov> =>
    preberi(`/api/podjetja/${podjetje}/projekti/nastavitve`, { ...jsonTelo(nastavitve), method: 'PUT' }),

  /** Shrani preslikavo stolpcev; prazna vrednost polje odstrani. */
  shraniPreslikavoStolpcev: (
    podjetje: string,
    preslikava: Record<string, string>,
    leto?: number,
  ): Promise<PreslikavaStolpcev> =>
    preberi(`/api/podjetja/${podjetje}/preslikava-stolpcev${leto === undefined ? '' : `?leto=${String(leto)}`}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ preslikava }),
    }),
};
